import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Paper,
    InputAdornment,
    IconButton,
    FormControlLabel,
    Checkbox,
    Link,
    Grid,
    CssBaseline
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import { login } from '../utils/helper';
import { tr } from 'date-fns/locale';
import { PropagateLoader } from "react-spinners";

const CSSProperties = {
    display: "block",
    margin: "0 auto",
    borderColor: "red",
};

// Styled components
const FullPageBackground = styled(Box)({
    minHeight: '100vh',
    backgroundImage: 'url(./images/bg1.jpg)', // Replace with your image path
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)', // Slightly transparent white
    borderRadius: theme.shape.borderRadius,
    width: '100%',
    maxWidth: 450,
}));

const StyledForm = styled('form')(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(1),
}));

const StyledSubmitButton = styled(Button)(({ theme }) => ({
    margin: theme.spacing(3, 0, 2),
    backgroundColor: '#00BFFF'
}));

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const Navigate = useNavigate();
    const location = useLocation();
    const [message, setMessage] = useState(location.state?.message || '');
    let [loading, setLoading] = useState(false);


    const handleSubmit = async () => {
        if (email === "") {
            toast.error('Please Enter Email', {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        } else if (password === "") {
            toast.error('Please Enter Password', {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        } else {
            let data = {
                "email": email,
                "password": password
            };
            try {
                setLoading(true);
                const response = await login('login', data);
                setLoading(false);
                if (response?.status === undefined) {
                    toast.success('Login Successful', {
                        position: "top-center",
                        autoClose: 500,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "light",
                        transition: Bounce,
                        onClose: () => {
                            if (response?.access_token !== null) {
                                sessionStorage.setItem('token', response?.access_token);
                                sessionStorage.setItem('type', response?.user?.type);
                                Navigate('/dashboard');
                            } else {
                                toast.error('Invalid Credentials', {
                                    position: "top-center",
                                    autoClose: 500,
                                    hideProgressBar: false,
                                    closeOnClick: false,
                                    pauseOnHover: true,
                                    draggable: true,
                                    progress: undefined,
                                    theme: "light",
                                    transition: Bounce,
                                });
                            }
                        }
                    });
                } else {
                    toast.error('Invalid Credentials', {
                        position: "top-center",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "light",
                        transition: Bounce,
                    });
                }
            } catch (error) {
                toast.error('Invalid credentials', {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                    transition: Bounce,
                });
            }
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    useEffect(() => {
        if (message !== '') {
            toast.error(message, {
                position: "top-center",
                autoClose: 500,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        }
    }, [message]);

    return (
        <>
            {loading && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100vw',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)', // Optional: semi-transparent white background
                    zIndex: 9999, // Ensure it's above other elements
                }}>
                    <PropagateLoader
                        color="#00BFFF"
                        loading={loading}
                        cssOverride={CSSProperties}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                    />
                </div>
            )}
            <CssBaseline />
            <ToastContainer />
            <FullPageBackground>
                <Container component="main" maxWidth="xs">
                    <StyledPaper elevation={3}>
                        <Box
                            component="img"
                            src="/images/logo.png" // Your logo path
                            alt="Company Logo"
                            sx={{
                                width: 120,
                                height: 'auto',
                                mb: 1,
                                maxWidth: '100%'
                            }}
                        />
                        <Typography component="p" variant="p" style={{ color: '#FFF' }}>
                            Salary Management System
                        </Typography>

                        <StyledForm noValidate>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label="Email Address"
                                name="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                sx={{
                                    backgroundColor: 'white', // Grey background
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#00BFFF', // Outline color
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#00BFFF', // Outline color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#00BFFF', // Outline color when focused
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#00BFFF', // Label color
                                        '&.Mui-focused': {
                                            color: '#00BFFF', // Label color when focused
                                        },
                                    },
                                }}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    backgroundColor: 'white', // Grey background
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#00BFFF', // Outline color
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#00BFFF', // Outline color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#00BFFF', // Outline color when focused
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#00BFFF', // Label color
                                        '&.Mui-focused': {
                                            color: '#00BFFF', // Label color when focused
                                        },
                                    },
                                }}
                            />
                            <StyledSubmitButton
                                onClick={handleSubmit}
                                type="button"
                                fullWidth
                                variant="contained"
                                color="primary"
                            >
                                Login
                            </StyledSubmitButton>

                        </StyledForm>
                    </StyledPaper>
                </Container>
            </FullPageBackground >
        </>
    );
};

export default LoginPage;